import { useEffect, useState, useRef } from "react";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { fmtDateTime } from "@/lib/time";

export default function NotificationBell() {
    const [data, setData] = useState({ items: [], unread: 0 });
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const wrapRef = useRef(null);

    const load = () => api.get("/notifications", { params: { unread_only: false } }).then(r => setData(r.data)).catch(() => {});

    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => { clearInterval(id); document.removeEventListener("mousedown", onClick); };
    }, []);

    const markAll = async () => {
        await api.post("/notifications/read-all");
        load();
    };

    const openOne = async (n) => {
        if (!n.is_read) await api.patch(`/notifications/${n.id}/read`);
        setOpen(false);
        if (n.link) navigate(n.link);
        load();
    };

    return (
        <div className="relative" ref={wrapRef}>
            <button onClick={() => setOpen(o => !o)} data-testid="notif-bell"
                className="relative text-muted-foreground hover:text-foreground p-2">
                <Bell className="w-4 h-4" strokeWidth={1.5}/>
                {data.unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[9px] min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center font-medium">
                        {data.unread > 99 ? "99+" : data.unread}
                    </span>
                )}
            </button>
            {open && (
                <div className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-full sm:mt-2 w-[calc(100vw-1rem)] sm:w-[380px] sm:max-w-[calc(100vw-2rem)] bg-muted border border-border shadow-2xl z-50">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                        <div className="overline">Notifications</div>
                        {data.unread > 0 && <button onClick={markAll} data-testid="notif-mark-all" className="text-xs text-primary hover:underline">Mark all read</button>}
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                        {data.items.length === 0 ? (
                            <div className="px-4 py-12 text-center text-sm text-muted-foreground">You're all caught up.</div>
                        ) : data.items.map(n => (
                            <button key={n.id} onClick={() => openOne(n)} data-testid={`notif-item-${n.id}`}
                                className={`w-full text-left px-4 py-3 border-b border-border hover:bg-background transition-colors ${!n.is_read ? "bg-background/50" : ""}`}>
                                <div className="flex items-start gap-3">
                                    {!n.is_read && <span className="w-1.5 h-1.5 bg-primary mt-1.5 shrink-0"></span>}
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm break-words">{n.title}</div>
                                        {n.body && <div className="text-xs text-muted-foreground mt-1 break-words line-clamp-2">{n.body}</div>}
                                        <div className="text-[10px] text-muted-foreground font-mono mt-1">{fmtDateTime(n.created_at)}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
